import {
  Backdrop,
  Box,
  ButtonBase,
  Divider,
  Drawer,
  Skeleton,
  Tooltip,
  Typography,
  useColorScheme,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import darkLogo from '../../assets/SEICLogoDark.svg';
import lightLogo from '../../assets/SEICLogoLight.svg';
import { useUser } from '../../hooks/useUser';
import { useGetPolicyDocumentQuery } from '../../services/portal/portalApi';
import ChangeLogButton from './ChangeLogButton';
import CustomerIcon from './CustomerIcon';
import DashboardIcon from './DashboardIcon';
import DealersIcon from './DealersIcon';
import ManageIcon from './ManageIcon';
import MeasureModelIcon from './MeasureModelIcon';
import MonitorIcon from './MonitorIcon';
import ProfileMenu from './ProfileMenu';
import ThemeSelector from './ThemeSelector';
import InCommandIcon from '../../custom-icons/IncommandIcon';
import MenuOpen from '../../custom-icons/MenuOpen';
import { ReactNode, useState } from 'react';
import MenuClose from '../../custom-icons/MenuClose';

export type AppNavProps = {
  open: boolean;
  items: {
    title: ReactNode;
    id: string;
    path?: string;
    icon?: JSX.Element;
    kind?: string;
  }[];
  onThemeChange?: (theme: string) => void;
  onNavigate?: (path: string) => void;
  onLogout?: () => void;
  afterItemClick?: () => void;
};
const icons = {
  overview: DashboardIcon,
  dashboard: DashboardIcon,
  monitor: MonitorIcon,
  measureModel: MeasureModelIcon,
  assetManager: ManageIcon,
  customers: CustomerIcon,
  dealers: DealersIcon,
};

const AppNav = ({
  open: propsOpen = true,
  items,
  onThemeChange,
  onNavigate,
  onLogout,
  afterItemClick,
}: AppNavProps) => {
  const { details, auth: user, isAuthenticated, isLoading, saveUserUIDetails } = useUser();
  const MuiTheme = useTheme();
  const { mode } = useColorScheme();
  const isMobile = useMediaQuery(MuiTheme.breakpoints.only('xs'));
  const [tabletNavOpen, setTabletNavOpen] = useState(false);

  const isTablet = useMediaQuery(MuiTheme.breakpoints.between('sm', 'md'));
  const open = isTablet ? tabletNavOpen : propsOpen;
  useGetPolicyDocumentQuery();

  const theme = details?.theme ?? 'dark';
  const navigate = useNavigate();

  const handleNavigate = (path: string | undefined) => {
    if (path) {
      if (onNavigate) {
        onNavigate?.(path);
      } else navigate(path);
    }
    if (isTablet) {
      setTabletNavOpen(false);
    }
  };

  const handleThemeChanged = async (theme: string) => {
    saveUserUIDetails({ theme });
    onThemeChange?.(theme);
  };

  const handleToggleResize = () => {
    saveUserUIDetails({ drawerOpen: !open });
  };

  const { pathname } = useLocation();

  const navContent = () => {
    return (
      <>
        {!isMobile && (
          <Box
            sx={{
              display: 'flex',
              flexShrink: 0,
              flexDirection: 'column',
              background: MuiTheme.palette.background.paperElevation0,
              boxShadow: MuiTheme.palette.shadows[32],
              marginBottom: '8px',
              borderRadius: '4px',
            }}
          >
            <ButtonBase
              className="toggleButton"
              flex={1}
              onClick={isTablet ? () => setTabletNavOpen(!tabletNavOpen) : handleToggleResize}
              sx={{
                height: '48px',
                display: 'flex',
                alignItems: 'center',
                color: MuiTheme.palette.text.secondary,
                justifyContent: open ? 'flex-end' : 'flex-start',
                padding: '12px 24px',
                borderRadius: '4px',
                '&:hover': {
                  background: `linear-gradient(0deg, ${
                    MuiTheme.palette.opac1
                  } 0%,  ${MuiTheme.palette.opac1} 100%),  ${
                    mode === 'dark' ? MuiTheme.palette.background.paperElevation0 : '#F0F6FF'
                  }`,
                  color: MuiTheme.palette.text.secondary,
                },
                '&:active': {
                  color: MuiTheme.palette.text.primary,
                  background: `linear-gradient(0deg, ${
                    MuiTheme.palette.opac2
                  } 0%,  ${MuiTheme.palette.opac2} 100%),  ${
                    mode === 'dark' ? MuiTheme.palette.background.paperElevation1 : '#F0F6FF'
                  }`,
                },
              }}
            >
              {!open ? <MenuOpen /> : <MenuClose />}
            </ButtonBase>
          </Box>
        )}

        <Box
          sx={{
            display: 'flex',
            background: MuiTheme.palette.background.paperElevation0,
            boxShadow: MuiTheme.palette.shadows[32],
            borderRadius: '4px',
            overflowY: 'auto',
            overflowX: 'hidden',
            flex: 1,
            '&::-webkit-scrollbar': {
              display: 'none',
            },
            '&:hover': {
              '&::-webkit-scrollbar': {
                display: 'block',
              },
            },
          }}
        >
          <Box
            sx={{
              width: open ? '280px' : '72px',
              transition: 'width 0.3s ease',
              padding: !isMobile ? '20px 8px' : '8px 8px 20px',
              display: 'flex',
              flexDirection: 'column',
              gap: '20px',
              flexShrink: 0,
            }}
          >
            {!isMobile && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  padding: '0 14px',
                  cursor: 'pointer',
                }}
                onClick={() => handleNavigate('/')}
                id="menu-icon"
              >
                <Box
                  sx={{ position: 'relative', overflow: 'hidden', width: '100%', height: '32px' }}
                >
                  <InCommandIcon
                    style={{
                      height: '32px',
                      width: 'auto',
                      position: 'absolute',
                      clipPath: open ? 'inset(0 100% 0 0)' : 'inset(0 0% 0 0)',
                      transition: 'clip-path 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                  />
                  <img
                    src={theme === 'dark' ? darkLogo : lightLogo}
                    alt="logo"
                    style={{
                      height: '32px',
                      width: 'auto',
                      position: 'absolute',
                      clipPath: open ? 'inset(0 0% 0 0)' : 'inset(0 100% 0 0)',
                      transition: 'clip-path 0.4s ease',
                    }}
                  />
                </Box>
              </Box>
            )}

            <Box
              style={{
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
              }}
            >
              {isLoading &&
                Array.from({ length: 3 }).map((_, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '0 16px',
                      margin: '9px 0px',
                      height: '38px',
                      gap: open ? '12px' : 0,
                    }}
                  >
                    <Skeleton width={24} height={'100%'} />
                    {open && <Skeleton sx={{ flex: 1, height: '100%' }} />}
                  </Box>
                ))}

              {items.map((item) => {
                const isActive = pathname.includes(item.path ?? '');
                const Icon = icons[item.id ?? 'overview'] || item?.icon || CustomerIcon;
                return item.kind === 'divider' ? (
                  <Divider
                    key={item.id}
                    sx={{
                      margin: `20px 16px`,
                    }}
                  />
                ) : (
                  <Tooltip title={!open ? item.title : ''} placement="right" key={item.id}>
                    <ButtonBase
                      key={item.id}
                      className={isActive ? 'active' : ''}
                      onClick={() => handleNavigate(item.path)}
                      sx={{
                        display: 'flex',
                        justifyContent: 'flex-start',
                        height: '56px',
                        padding: '9px 16px',
                        borderRadius: '2px',
                        gap: open ? '12px' : 0,
                        transition: 'gap 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                        color: isActive ? 'text.selectedbutton' : 'text.secondary',
                        '&.active': {
                          borderRadius: '2px',
                          background: MuiTheme.palette.opacblack2,
                          color: MuiTheme.palette.text.selectedbutton,
                          boxShadow: MuiTheme.palette.elevations.secondaryButtonPressed2,
                          '&:hover': {
                            background: MuiTheme.palette.opacblack3,
                            boxShadow: MuiTheme.palette.elevations.hoverButtonPressed,
                          },
                        },
                        '&:hover': {
                          background: `linear-gradient(0deg, ${
                            MuiTheme.palette.opac1
                          } 0%,  ${MuiTheme.palette.opac1} 100%),  ${
                            mode === 'dark'
                              ? MuiTheme.palette.background.paperElevation0
                              : '#F0F6FF'
                          }`,
                          boxShadow: MuiTheme.palette.elevations.hoverButton,
                        },
                      }}
                    >
                      <Icon style={{ width: 24, height: 24, flexShrink: 0 }} />
                      <Typography
                        variant="body2"
                        style={{
                          textAlign: 'left',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          fontWeight: 500,
                          clipPath: open ? 'inset(0 0% 0 0)' : 'inset(0 100% 0 0)',
                          transition: 'clip-path 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                        }}
                      >
                        {item.title}
                      </Typography>
                    </ButtonBase>
                  </Tooltip>
                );
              })}
            </Box>
            <Box
              className="bottom-actions"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
              }}
            >
              <ProfileMenu onNavigate={onNavigate} size={open ? 'large' : 'small'} />
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '10px',
                  width: '100%',
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    height: '56px',
                    width: '100%',
                  }}
                >
                  <ThemeSelector
                    onThemeChange={handleThemeChanged}
                    value={theme}
                    size={open ? 'large' : 'small'}
                  />
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    padding: '0px 16px',
                    gap: '20px',
                    flexDirection: 'column',
                  }}
                >
                  <Divider />
                  <ChangeLogButton size={open ? 'large' : 'small'} />
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </>
    );
  };

  return (
    <>
      <Box
        component="nav"
        sx={{
          height: '100%',
          width: isTablet ? '72px' : 'auto',
          background: MuiTheme.palette.background.default,
          display: 'flex',
          flexDirection: 'column',
          transition: 'width 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        {isTablet ? (
          <>
            <Drawer
              variant="permanent"
              ModalProps={{
                keepMounted: true,
              }}
              open={open}
              anchor="left"
              onClose={() => setTabletNavOpen(false)}
              sx={{
                zIndex: (theme) => theme.zIndex.appBar - 1,
                '& .MuiDrawer-paper': {
                  width: open ? '280px' : '72px',
                  transition: 'width 0.3s ease',
                  background: MuiTheme.palette.background.default,
                  borderRight: 'none',
                },
              }}
            >
              {navContent()}
            </Drawer>
            <Backdrop
              open={open}
              onClick={() => setTabletNavOpen(false)}
              sx={{ zIndex: (theme) => theme.zIndex.appBar - 2 }}
            />
          </>
        ) : (
          navContent()
        )}
      </Box>
    </>
  );
};

export default AppNav;
